/**
 * structuredFromEditor — derive the same shape as `/crdt/structured`
 * directly from a live TipTap editor's PM doc. Lets the Editor compute
 * annotation-highlights (and any other section-derived UI) from the
 * Yjs-synced doc-tree it already has, instead of polling HTTP.
 *
 * Requires ParagraphWithAuthor to register `section_id` / `section_type`
 * / `section_role` / `section_attrs` on the paragraph node, otherwise
 * y-tiptap drops those attrs in the Yjs ↔ PM bridge.
 */

import type { Editor } from '@tiptap/react'

interface StructuredParagraph {
  index: number
  text: string
  author_id?: string | null
  author_name?: string | null
  author_color?: string | null
  is_heading?: boolean
  heading_level?: number | null
  block_id?: string | null
  commit_id?: string | null
  created_at?: string | null
  section_id?: string | null
  section_type?: 'product' | 'question' | 'task' | 'annotation' | null
  section_role?: 'start' | 'content' | 'reply' | 'end' | null
  section_attrs?: Record<string, unknown> | null
}

export function structuredFromEditor(editor: Editor): StructuredParagraph[] {
  const out: StructuredParagraph[] = []
  const doc = editor.state.doc
  let i = 0
  doc.forEach((node) => {
    if (node.type.name !== 'paragraph' && node.type.name !== 'heading') return
    const attrs = (node.attrs || {}) as Record<string, unknown>
    let sectionAttrs: Record<string, unknown> | null = null
    const rawAttrs = attrs.section_attrs
    if (typeof rawAttrs === 'string' && rawAttrs.length > 0) {
      try {
        const parsed = JSON.parse(rawAttrs)
        if (parsed && typeof parsed === 'object') {
          sectionAttrs = parsed as Record<string, unknown>
        }
      } catch {
        sectionAttrs = null
      }
    } else if (rawAttrs && typeof rawAttrs === 'object') {
      sectionAttrs = rawAttrs as Record<string, unknown>
    }
    const headingLevel =
      node.type.name === 'heading'
        ? ((attrs.level as number | undefined) ?? null)
        : null
    out.push({
      index: i++,
      text: node.textContent || '',
      author_id: (attrs.author_id as string | null) ?? null,
      author_name: (attrs.author_name as string | null) ?? null,
      author_color: (attrs.author_color as string | null) ?? null,
      is_heading: node.type.name === 'heading',
      heading_level: headingLevel,
      block_id: (attrs.block_id as string | null) ?? null,
      commit_id: (attrs.commit_id as string | null) ?? null,
      created_at: (attrs.created_at as string | null) ?? null,
      section_id: (attrs.section_id as string | null) ?? null,
      section_type:
        (attrs.section_type as
          | 'product'
          | 'question'
          | 'task'
          | 'annotation'
          | null) ?? null,
      section_role:
        (attrs.section_role as
          | 'start'
          | 'content'
          | 'reply'
          | 'end'
          | null) ?? null,
      section_attrs: sectionAttrs,
    })
  })
  return out
}
